import { PlanungAdminRoles, PlanungViewerRoles } from '@bp/pim-auth-constants';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { usePimProfilesQuery } from '../../../types/planung-graphql-client-defs';
import {
  isPlanungDeputatePermission,
  isPlanungPermission,
  isPlanungSubstitutionPermission,
  isPlanungTimetablePermission,
} from '../../../utils/typeguards';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { UserPermissions } from './UserPermissions';
import styles from './UserPermissionsForm.module.scss';

// 'no-access' size checkbox do not accept null
export type UserPermissionsFormType = {
  isAdmin: boolean;
  deputate: 'no-access' | PlanungViewerRoles.DeputatViewer | PlanungAdminRoles.DeputatAdmin;
  timetable: 'no-access' | PlanungViewerRoles.StundenViewer | PlanungAdminRoles.StundenAdmin;
  substitution: 'no-access' | PlanungViewerRoles.VertretungViewer | PlanungAdminRoles.VertretungAdmin;
};

type UserPermissionsFormProps = {
  profileUuid?: string | null;
  onPermissionsChanged: (
    permissions: Array<PlanungAdminRoles | PlanungViewerRoles>,
    profileUuid?: string | null,
  ) => Promise<void>;
  onClose?: () => void;
};

export const UserPermissionsForm = ({ profileUuid, onClose, onPermissionsChanged }: UserPermissionsFormProps) => {
  const { pimAuthClaims } = useAuthClaims();

  const [{ data: pimProfilesData }] = usePimProfilesQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
    },
  });

  const currentProfile = pimProfilesData?.pimProfiles.find((profile) => profile.uuid === profileUuid);

  const currentDeputatePermission =
    currentProfile?.permissions.find((permission) => {
      return isPlanungPermission(permission) && isPlanungDeputatePermission(permission);
    }) ?? 'no-access';

  const currentTimetablePermission =
    currentProfile?.permissions.find((permission) => {
      return isPlanungPermission(permission) && isPlanungTimetablePermission(permission);
    }) ?? 'no-access';

  const currentSubstitutionPermission =
    currentProfile?.permissions.find((permission) => {
      return isPlanungPermission(permission) && isPlanungSubstitutionPermission(permission);
    }) ?? 'no-access';

  const initialValues: UserPermissionsFormType = {
    isAdmin: currentProfile?.permissions.includes(PlanungAdminRoles.Admin) ?? false,
    deputate: currentDeputatePermission,
    timetable: currentTimetablePermission,
    substitution: currentSubstitutionPermission,
  };

  async function handleSubmit(values: UserPermissionsFormType, formHelpers: FormikHelpers<UserPermissionsFormType>) {
    const permissions: Array<PlanungAdminRoles | PlanungViewerRoles> = [];
    if (values.isAdmin) {
      permissions.push(PlanungAdminRoles.Admin);
    } else {
      if (values.deputate !== 'no-access') {
        permissions.push(values.deputate);
      }
      if (values.substitution !== 'no-access') {
        permissions.push(values.substitution);
      }
      if (values.timetable !== 'no-access') {
        permissions.push(values.timetable);
      }
    }
    await onPermissionsChanged(permissions, profileUuid);
    formHelpers.resetForm();
    if (onClose) {
      onClose();
    }
  }

  return (
    <Formik<UserPermissionsFormType> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ errors, dirty, setFieldValue, values }) => {
        return (
          <Form>
            <UserPermissions
              setFieldValue={async (key, value) => {
                await setFieldValue(key, value);
              }}
              values={values}
            />

            <ModalBottomButtons
              closeButton={{ callback: onClose }}
              submitButton={{ disabled: !dirty }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
